import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Info, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import PromotionDialog, { AUDIENCES, PROMO_TIERS, offerLine } from "@/components/admin/PromotionDialog";
import PromoPreviewDialog from "@/components/admin/PromoPreviewDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const STATE_TONE = { draft: "neutral", scheduled: "brand", active: "success", paused: "warning", ended: "neutral" };
const audienceText = (a) => (a.type === "tier" ? `${PROMO_TIERS[a.tier] || a.tier} members` : a.type === "zips" ? `ZIPs ${a.zips.join(", ")}` : AUDIENCES[a.type]);
const ACTIONS = {
  schedule: { label: "Schedule", desc: "It goes live at the start time and ends on its own.", destructive: false },
  pause: { label: "Pause", desc: "The offers stop showing until you schedule it again.", destructive: true },
  end: { label: "End now", desc: "It stops for good and can't be edited or restarted.", destructive: true },
  delete: { label: "Delete", desc: "The promotion is removed from this list. Past purchases keep their record. This can't be undone.", destructive: true },
};

function PromoRow({ p, onEdit, onAct, onPreview }) {
  const live = p.status !== "ended";
  return (
    <li className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-start" data-testid={`promo-row-${p.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><p className="font-medium">{p.name}</p><Pill tone={STATE_TONE[p.state]} className="capitalize" data-testid={`promo-state-${p.id}`}>{p.state}</Pill></div>
        <p className="cw-soft mt-1 text-sm">{formatDate(p.starts_at)} → {formatDate(p.ends_at)} · {audienceText(p.audience)}</p>
        <ul className="mt-2 flex flex-wrap gap-1.5">{p.offers.map((o) => <li key={o.id}><Pill>{offerLine(o)}</Pill></li>)}</ul>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" onClick={() => onPreview(p)} data-testid={`promo-preview-button-${p.id}`}><Eye />Preview as user</Button>
        {live && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`promo-edit-button-${p.id}`}><PencilSimple />Edit</Button>}
        {(p.status === "draft" || p.status === "paused") && <Button size="sm" onClick={() => onAct(p, "schedule")} data-testid={`promo-schedule-button-${p.id}`}>Schedule</Button>}
        {p.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => onAct(p, "pause")} data-testid={`promo-pause-button-${p.id}`}>Pause</Button>}
        {live && <Button size="sm" variant="outline" className="cw-error" onClick={() => onAct(p, "end")} data-testid={`promo-end-button-${p.id}`}>End</Button>}
        {(p.status === "draft" || p.state === "ended") && <Button size="sm" variant="ghost" className="cw-error" onClick={() => onAct(p, "delete")} data-testid={`promo-delete-button-${p.id}`}><Trash />Delete</Button>}
      </div>
    </li>
  );
}

export default function Promotions() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/promotions");
  const [dialog, setDialog] = useState(null);
  const [ask, setAsk] = useState(null);
  const [preview, setPreview] = useState(null);
  const upsert = (promo, editing) => {
    setData((d) => ({ ...d, items: editing ? d.items.map((x) => (x.id === promo.id ? promo : x)) : [promo, ...d.items] }));
    toast.success(editing ? "Promotion saved." : "Draft created.");
  };
  const act = async () => {
    if (ask.action === "delete") {
      try { await api(`/admin/promotions/${ask.p.id}`, { method: "DELETE" }); }
      catch (e) { throw e.code === "promotion_live" ? new Error("Pause and end this promotion before deleting it.") : e; }
      setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== ask.p.id) }));
      toast.success("Promotion deleted.");
      return;
    }
    const r = await api(`/admin/promotions/${ask.p.id}/${ask.action}`, { method: "POST" });
    upsert(r.promotion, true);
  };

  return (
    <div data-testid="admin-promotions-page">
      <PageHeader title="Promotions" description="Time-boxed store offers for an audience. Real store prices only, and a plain end date."
        actions={<Button onClick={() => setDialog({ promo: null })} disabled={!data} data-testid="promo-create-button"><Plus />New promotion</Button>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data && !data.flag_enabled && (
          <div className="cw-tone-warning mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="promo-flag-off"><Info size={18} className="mt-0.5 shrink-0" />
            <p>The <span className="font-mono">promotions</span> flag is off, so no offers show in the app yet. <Link to="/admin/flags" className="underline">Feature flags</Link></p></div>
        )}
        <Panel className="overflow-hidden">
          {data?.items?.length ? <ul className="divide-y divide-border" data-testid="promos-list">{data.items.map((p) => (
            <PromoRow key={p.id} p={p} onEdit={(x) => setDialog({ promo: x })} onAct={(x, action) => setAsk({ p: x, action })} onPreview={setPreview} />
          ))}</ul> : <Empty title="No promotions yet" text="Add store offers in Pricing, then create a promotion." testId="promos-empty" />}
        </Panel>
      </LoadState>
      {dialog && <PromotionDialog open onOpenChange={(o) => !o && setDialog(null)} promo={dialog.promo} offers={data?.offers || []} onSaved={upsert} />}
      <PromoPreviewDialog promo={preview} onOpenChange={(o) => !o && setPreview(null)} />
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} testId="promo-action-confirm-dialog" onConfirm={act}
        destructive={ask && ACTIONS[ask.action].destructive} confirmLabel={ask && ACTIONS[ask.action].label}
        title={ask ? `${ACTIONS[ask.action].label} “${ask.p.name}”?` : ""} description={ask && ACTIONS[ask.action].desc} />
    </div>
  );
}
